import { useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import LogoCropModal from "@/components/LogoCropModal";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Pro-only "Custom Look" section inside RoomSettings:
 *  - Upload / remove custom LOGO (rendered faded behind the buzzer)
 *  - Upload / remove custom BACKGROUND image
 *  - Pick / reset custom TEXT COLOR
 *
 * Free hosts see a locked preview with an upgrade link.
 */
const CustomizationPanel = ({
  isPro,
  roomCode,
  hasCustomLogo,
  hasCustomBackground,
  customTextColor,
  logoVersion,
  bgVersion,
  onClose,
}) => {
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'logo' | 'bg' | 'color' | null
  const [error, setError] = useState(null);
  const [colorDraft, setColorDraft] = useState(customTextColor || "#FFEA00");
  const [pendingLogoFile, setPendingLogoFile] = useState(null); // file waiting for crop

  const authHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const upload = async (key, form) => {
    setError(null);
    setBusy(key);
    try {
      await axios.post(
        `${backendUrl}/api/rooms/${roomCode}/customization`,
        form,
        {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Something went wrong uploading — try again.";
      setError(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    // Cropper handles arbitrary source size; we crop client-side and upload a
    // small square PNG (~<200KB typically). Show a friendly warning for absurd files.
    if (file.size > 15 * 1024 * 1024) {
      setError("That image is huge — pick one under 15MB and try again.");
      return;
    }
    setError(null);
    setPendingLogoFile(file);
  };

  const handleCroppedLogo = async (blob) => {
    setPendingLogoFile(null);
    if (!blob) return;
    if (blob.size > 2 * 1024 * 1024) {
      setError("Cropped logo is over 2MB — try a smaller source image.");
      return;
    }
    const fd = new FormData();
    fd.append("logo", blob, "logo.png");
    await upload("logo", fd);
  };

  const handleBgFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Background must be under 5MB");
      return;
    }
    const fd = new FormData();
    fd.append("background", file);
    upload("bg", fd);
  };

  const removeLogo = () => {
    const fd = new FormData();
    fd.append("remove_logo", "true");
    upload("logo", fd);
  };

  const removeBg = () => {
    const fd = new FormData();
    fd.append("remove_background", "true");
    upload("bg", fd);
  };

  const applyColor = () => {
    const fd = new FormData();
    fd.append("text_color", colorDraft);
    upload("color", fd);
  };

  const resetColor = () => {
    const fd = new FormData();
    fd.append("remove_text_color", "true");
    upload("color", fd);
  };

  // ----- Locked-for-free view -----
  if (!isPro) {
    return (
      <div className="px-5 py-5 border-t border-white/5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white/90 font-semibold text-sm uppercase tracking-wider">
            Custom look
          </h3>
          <span
            className="text-[10px] uppercase tracking-wider bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/40 px-2 py-0.5 rounded-full"
            data-testid="customization-pro-badge"
          >
            Pro only
          </span>
        </div>
        <p className="text-[#A1A1AA] text-xs mb-3">
          Upload your own logo behind the buzzer, your own background image, and
          set a custom text color.
        </p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {["🖼️ Logo", "🎨 Background", "🅰️ Text color"].map((label) => (
            <div
              key={label}
              className="text-center bg-white/5 border border-white/10 rounded-xl py-4 text-white/60 text-xs opacity-50"
            >
              <div className="text-2xl mb-1">🔒</div>
              {label}
            </div>
          ))}
        </div>
        <Link
          to="/upgrade"
          onClick={onClose}
          className="block w-full text-center px-4 py-2 bg-[#FFEA00] text-black text-sm font-bold rounded-xl hover:scale-[1.02] active:scale-95 transition-transform"
          data-testid="customization-upgrade-link"
        >
          ⭐ Upgrade to unlock
        </Link>
      </div>
    );
  }

  const logoUrl = hasCustomLogo
    ? `${backendUrl}/api/rooms/${roomCode}/logo?v=${logoVersion}`
    : null;
  const bgUrl = hasCustomBackground
    ? `${backendUrl}/api/rooms/${roomCode}/background?v=${bgVersion}`
    : null;

  return (
    <div className="px-5 py-5 border-t border-white/5" data-testid="customization-panel">
      <h3 className="text-white/90 font-semibold text-sm uppercase tracking-wider mb-1">
        Custom look
      </h3>
      <p className="text-[#A1A1AA] text-xs mb-4">
        Changes apply live for everyone in the room.
      </p>

      {error && (
        <div
          className="mb-3 px-3 py-2 rounded-lg bg-[#FF0055]/15 border border-[#FF0055]/30 text-[#FF0055] text-xs"
          data-testid="customization-error"
        >
          {error}
        </div>
      )}

      {/* Logo */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white text-sm font-semibold">🖼️ Buzzer Logo</span>
          <span className="text-[10px] text-[#A1A1AA]">Max 2MB · PNG, JPG, WebP</span>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="w-16 h-16 rounded-full border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden shrink-0 relative"
            style={{ background: "#FF0055" }}
          >
            {logoUrl ? (
              <>
                <img
                  src={logoUrl}
                  alt="logo preview"
                  className="absolute inset-0 w-full h-full object-cover opacity-55"
                  data-testid="logo-preview"
                />
                <span
                  className="relative z-10 text-white text-[10px] font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                  style={{ fontFamily: "Anton, sans-serif" }}
                >
                  TAP
                </span>
              </>
            ) : (
              <span className="text-white text-xs opacity-70">TAP</span>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                handleLogoFile(e.target.files?.[0]);
                e.target.value = "";
              }}
              data-testid="logo-file-input"
            />
            <button
              onClick={() => logoInputRef.current?.click()}
              disabled={busy === "logo"}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-all active:scale-95 disabled:opacity-50"
              data-testid="upload-logo-button"
            >
              {busy === "logo"
                ? "Uploading…"
                : hasCustomLogo
                ? "Replace logo"
                : "Upload logo"}
            </button>
            {hasCustomLogo && (
              <button
                onClick={removeLogo}
                disabled={busy === "logo"}
                className="w-full px-3 py-1.5 bg-[#FF0055]/15 text-[#FF0055] border border-[#FF0055]/30 text-xs font-semibold rounded-lg hover:bg-[#FF0055]/25 transition-all disabled:opacity-50"
                data-testid="remove-logo-button"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Background */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white text-sm font-semibold">🎨 Background</span>
          <span className="text-[10px] text-[#A1A1AA]">Max 5MB · PNG, JPG, WebP</span>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="w-24 h-16 rounded-lg border border-white/10 bg-white/5 overflow-hidden shrink-0"
            style={
              bgUrl
                ? { background: `url('${bgUrl}') center/cover` }
                : {
                    background:
                      "linear-gradient(135deg, #18181B 0%, #3F3F46 100%)",
                  }
            }
            data-testid="bg-preview"
          />
          <div className="flex-1 flex flex-col gap-2">
            <input
              ref={bgInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => handleBgFile(e.target.files?.[0])}
              data-testid="bg-file-input"
            />
            <button
              onClick={() => bgInputRef.current?.click()}
              disabled={busy === "bg"}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-all active:scale-95 disabled:opacity-50"
              data-testid="upload-bg-button"
            >
              {busy === "bg"
                ? "Uploading…"
                : hasCustomBackground
                ? "Replace background"
                : "Upload background"}
            </button>
            {hasCustomBackground && (
              <button
                onClick={removeBg}
                disabled={busy === "bg"}
                className="w-full px-3 py-1.5 bg-[#FF0055]/15 text-[#FF0055] border border-[#FF0055]/30 text-xs font-semibold rounded-lg hover:bg-[#FF0055]/25 transition-all disabled:opacity-50"
                data-testid="remove-bg-button"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Text color */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-white text-sm font-semibold">🅰️ Buzzer Text Color</span>
          {customTextColor && (
            <span
              className="text-[10px] text-white font-mono"
              data-testid="current-text-color"
            >
              {customTextColor}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={colorDraft}
            onChange={(e) => setColorDraft(e.target.value)}
            className="w-14 h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer shrink-0"
            data-testid="text-color-picker"
          />
          <input
            type="text"
            value={colorDraft}
            onChange={(e) => setColorDraft(e.target.value)}
            maxLength={7}
            placeholder="#FFEA00"
            className="flex-1 px-3 py-2 bg-white/10 border border-white/20 text-white text-sm font-mono rounded-lg focus:outline-none focus:border-white/40"
            data-testid="text-color-input"
          />
          <button
            onClick={applyColor}
            disabled={busy === "color"}
            className="px-3 py-2 bg-[#00F0FF] text-black text-sm font-bold rounded-lg hover:bg-[#33F3FF] transition-all active:scale-95 disabled:opacity-50"
            data-testid="apply-color-button"
          >
            {busy === "color" ? "…" : "Apply"}
          </button>
        </div>
        {customTextColor && (
          <button
            onClick={resetColor}
            disabled={busy === "color"}
            className="mt-2 text-[11px] text-[#A1A1AA] underline hover:text-white transition"
            data-testid="reset-color-button"
          >
            Reset to default white
          </button>
        )}
      </div>

      {/* Circular crop modal — appears only after picking a logo file */}
      <LogoCropModal
        file={pendingLogoFile}
        onCancel={() => setPendingLogoFile(null)}
        onCropped={handleCroppedLogo}
      />
    </div>
  );
};

export default CustomizationPanel;
